import { useState, useEffect } from 'react';
import { apiService, AuthenticationError } from '../services/api';
import { User, Notification, AuditLog } from '../types';

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  isLoading: boolean;
  error: string | null;
  notifications: Notification[];
  unreadCount: number;
  permissions: string[];
  auditLogs: AuditLog[];
}

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    isLoading: true,
    error: null,
    notifications: [],
    unreadCount: 0,
    permissions: [],
    auditLogs: []
  });

  // Check for existing session on mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      console.log('Checking auth status...');

      try {
        const token = localStorage.getItem('planner_token');
        if (!token) {
          console.log('No auth token found');
          setAuthState({
            isAuthenticated: false,
            user: null,
            isLoading: false,
            error: null,
            notifications: [],
            unreadCount: 0,
            permissions: [],
            auditLogs: []
          });
          return;
        }

        const userData = await apiService.getCurrentUser();
        if (userData) {
          const user: User = {
            id: userData.id,
            accountId: userData.account_id,
            displayName: userData.display_name,
            emailAddress: userData.email_address,
            avatarUrl: userData.avatar_url || '',
            active: userData.active,
            timeZone: userData.time_zone,
            locale: userData.locale,
            groups: userData.groups || [],
            permissions: userData.permissions || []
          };

          const permissions = getUserPermissions(user);
          const auditLogs = generateMockAuditLogs(user.id);

          setAuthState({
            isAuthenticated: true,
            user,
            isLoading: false,
            error: null,
            notifications: [],
            unreadCount: 0,
            permissions,
            auditLogs
          });
          return;
        }

        setAuthState({
          isAuthenticated: false,
          user: null,
          isLoading: false,
          error: null,
          notifications: [],
          unreadCount: 0,
          permissions: [],
          auditLogs: []
        });
      } catch (error) {
        console.error('Error checking auth status:', error);
        
        if (error instanceof AuthenticationError || (error && (error as any).status === 401)) {
          setAuthState({
            isAuthenticated: false,
            user: null,
            isLoading: false,
            error: 'Session expired. Please log in again.',
            notifications: [],
            unreadCount: 0,
            permissions: [],
            auditLogs: []
          });
          return;
        }

        // Do NOT automatically log out on connection issues.
        // Try to load cached user from localStorage
        const cachedUserStr = localStorage.getItem('planner_user');
        if (cachedUserStr) {
          try {
            const userData = JSON.parse(cachedUserStr);
            const user: User = {
              id: userData.id,
              accountId: userData.account_id,
              displayName: userData.display_name,
              emailAddress: userData.email_address,
              avatarUrl: userData.avatar_url || '',
              active: userData.active,
              timeZone: userData.time_zone,
              locale: userData.locale,
              groups: userData.groups || [],
              permissions: userData.permissions || []
            };

            const permissions = getUserPermissions(user);
            const auditLogs = generateMockAuditLogs(user.id);

            setAuthState({
              isAuthenticated: true,
              user,
              isLoading: false,
              error: null,
              notifications: [],
              unreadCount: 0,
              permissions,
              auditLogs
            });
            return;
          } catch (e) {
            console.error('Failed to parse cached user:', e);
          }
        }

        setAuthState({
          isAuthenticated: false,
          user: null,
          isLoading: false,
          error: 'Failed to connect to authentication server',
          notifications: [],
          unreadCount: 0,
          permissions: [],
          auditLogs: []
        });
      }
    };

    checkAuthStatus();
  }, []);

  // Silent token refresh: refresh the token every 12 hours to keep the session alive indefinitely.
  // The backend token expires after 365 days, but we proactively refresh to ensure
  // the user never gets logged out unless they manually click logout.
  useEffect(() => {
    const token = localStorage.getItem('planner_token');
    if (!token) return;

    const REFRESH_INTERVAL_MS = 12 * 60 * 60 * 1000; // 12 hours

    const refreshTokenSilently = async () => {
      try {
        console.log('Silent token refresh: attempting...');
        const success = await apiService.refreshToken();
        if (success) {
          console.log('Silent token refresh: success');
        } else {
          console.warn('Silent token refresh: failed, will retry next interval');
        }
      } catch (error) {
        console.warn('Silent token refresh: error, will retry next interval', error);
      }
    };

    // Refresh immediately on mount if the user is authenticated
    // (this handles the case where the app was closed for a while)
    refreshTokenSilently();

    // Set up periodic refresh
    const intervalId = setInterval(refreshTokenSilently, REFRESH_INTERVAL_MS);

    // Also refresh when the tab becomes visible again (user returns after being away)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && localStorage.getItem('planner_token')) {
        console.log('Tab became visible, refreshing token...');
        refreshTokenSilently();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [authState.isAuthenticated]);

  const login = async (email: string, password: string): Promise<boolean> => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      console.log('Attempting login for:', email);
      const data = await apiService.login(email, password);
      const userData = data.user;
      if (!userData) {
        setAuthState(prev => ({
          ...prev,
          isLoading: false,
          error: 'Failed to load user profile'
        }));
        return false;
      }

      localStorage.setItem('planner_token', data.access_token);
      localStorage.setItem('planner_user', JSON.stringify(userData));

      const user: User = {
        id: userData.id,
        accountId: userData.account_id,
        displayName: userData.display_name,
        emailAddress: userData.email_address,
        avatarUrl: userData.avatar_url || '',
        active: userData.active,
        timeZone: userData.time_zone,
        locale: userData.locale,
        groups: userData.groups || [],
        permissions: userData.permissions || []
      };

      const permissions = getUserPermissions(user);
      const auditLogs = generateMockAuditLogs(user.id);

      setAuthState({
        isAuthenticated: true,
        user,
        isLoading: false,
        error: null,
        notifications: [],
        unreadCount: 0,
        permissions,
        auditLogs
      });

      console.log('Login successful');
      return true;
    } catch (error) {
      console.error('Login failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage
      }));
      return false;
    }
  };

  const socialLogin = async (provider: 'google' | 'github' | 'microsoft'): Promise<boolean> => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      console.log(`Attempting ${provider} login`);
      const errorMessage = 'Social login is not yet implemented';
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage
      }));
      return false;
    } catch (error) {
      console.error(`${provider} login failed:`, error);
      const errorMessage = error instanceof Error ? error.message : `${provider} login failed`;
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage
      }));
      return false;
    }
  };

  const register = async (displayName: string, email: string, password: string): Promise<boolean> => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      console.log('Attempting registration for:', email);
      const data = await apiService.register({
        display_name: displayName,
        email_address: email,
        password
      });

      const userData = data.user;
      if (!userData) {
        setAuthState(prev => ({
          ...prev,
          isLoading: false,
          error: 'Failed to load user profile'
        }));
        return false;
      }

      localStorage.setItem('planner_token', data.access_token);
      localStorage.setItem('planner_user', JSON.stringify(userData));

      const user: User = {
        id: userData.id,
        accountId: userData.account_id,
        displayName: userData.display_name,
        emailAddress: userData.email_address,
        avatarUrl: userData.avatar_url || '',
        active: userData.active,
        timeZone: userData.time_zone,
        locale: userData.locale,
        groups: userData.groups || [],
        permissions: userData.permissions || []
      };

      const permissions = getUserPermissions(user);
      const auditLogs = generateMockAuditLogs(user.id);

      setAuthState({
        isAuthenticated: true,
        user,
        isLoading: false,
        error: null,
        notifications: [],
        unreadCount: 0,
        permissions,
        auditLogs
      });

      console.log('Registration successful');
      return true;
    } catch (error) {
      console.error('Registration failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Registration failed';
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage
      }));
      return false;
    }
  };

  const logout = async () => {
    console.log('Logging out user');

    try {
      await apiService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }

    setAuthState({
      isAuthenticated: false,
      user: null,
      isLoading: false,
      error: null,
      notifications: [],
      unreadCount: 0,
      permissions: [],
      auditLogs: []
    });

    console.log('Auth state cleared');
  };

  const clearError = () => {
    setAuthState(prev => ({ ...prev, error: null }));
  };

  const markNotificationAsRead = (notificationId: string) => {
    setAuthState(prev => ({
      ...prev,
      notifications: prev.notifications.map(n =>
        n.id === notificationId ? { ...n, read: true } : n
      ),
      unreadCount: prev.notifications.filter(n => n.id !== notificationId && !n.read).length
    }));
  };

  const markAllNotificationsAsRead = () => {
    setAuthState(prev => ({
      ...prev,
      notifications: prev.notifications.map(n => ({ ...n, read: true })),
      unreadCount: 0
    }));
  };

  const sendOtp = async (email: string): Promise<{ success: boolean; message: string; otp_test_fallback?: string }> => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const data = await apiService.sendOtp(email);
      setAuthState(prev => ({ ...prev, isLoading: false }));
      return { success: true, message: data.message, otp_test_fallback: data.otp_test_fallback };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send OTP';
      setAuthState(prev => ({ ...prev, isLoading: false, error: errorMessage }));
      return { success: false, message: errorMessage };
    }
  };

  const otpLogin = async (email: string, otp: string): Promise<boolean> => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      console.log('Attempting OTP login for:', email);
      const data = await apiService.otpLogin(email, otp);
      const userData = data.user;
      if (!userData) {
        setAuthState(prev => ({
          ...prev,
          isLoading: false,
          error: 'Failed to load user profile'
        }));
        return false;
      }

      localStorage.setItem('planner_token', data.access_token);
      localStorage.setItem('planner_user', JSON.stringify(userData));

      const user: User = {
        id: userData.id,
        accountId: userData.account_id,
        displayName: userData.display_name,
        emailAddress: userData.email_address,
        avatarUrl: userData.avatar_url || '',
        active: userData.active,
        timeZone: userData.time_zone,
        locale: userData.locale,
        groups: userData.groups || [],
        permissions: userData.permissions || []
      };

      const permissions = getUserPermissions(user);
      const auditLogs = generateMockAuditLogs(user.id);

      setAuthState({
        isAuthenticated: true,
        user,
        isLoading: false,
        error: null,
        notifications: [],
        unreadCount: 0,
        permissions,
        auditLogs
      });

      console.log('OTP Login successful');
      return true;
    } catch (error) {
      console.error('OTP Login failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'OTP Verification failed';
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage
      }));
      return false;
    }
  };

  return {
    ...authState,
    login,
    register,
    socialLogin,
    logout,
    clearError,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    sendOtp,
    otpLogin
  };
};

const getUserPermissions = (user: User): string[] => {
  const basePermissions = [
    'VIEW_ISSUES',
    'CREATE_ISSUES',
    'EDIT_OWN_ISSUES',
    'COMMENT_ISSUES',
    'VIEW_PROJECTS'
  ];

  if (user.groups.includes('jira-administrators')) {
    return [
      ...basePermissions,
      'ADMIN_PROJECTS',
      'ADMIN_USERS',
      'ADMIN_SYSTEM',
      'VIEW_AUDIT_LOGS',
      'MANAGE_PERMISSIONS',
      'MANAGE_INTEGRATIONS',
      'MANAGE_WORKFLOWS',
      'BULK_OPERATIONS',
      'ADVANCED_SEARCH',
      'EXPORT_DATA'
    ];
  }

  if (user.groups.includes('developers')) {
    return [
      ...basePermissions,
      'EDIT_ALL_ISSUES',
      'DELETE_ISSUES',
      'MANAGE_SPRINTS',
      'VIEW_REPORTS',
      'TIME_TRACKING'
    ];
  }

  return basePermissions;
};

const generateMockAuditLogs = (userId: string): AuditLog[] => {
  return [
    {
      id: '1',
      userId,
      action: 'ISSUE_CREATED',
      entityType: 'issue',
      entityId: 'ECOM-15',
      changes: {
        summary: { from: null, to: 'Implement payment gateway' },
        status: { from: null, to: 'to-do' }
      },
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      ipAddress: '192.168.1.100',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
    },
    {
      id: '2',
      userId,
      action: 'ISSUE_UPDATED',
      entityType: 'issue',
      entityId: 'ECOM-12',
      changes: {
        status: { from: 'in-progress', to: 'in-review' },
        assignee: { from: userId, to: '2' }
      },
      timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
      ipAddress: '192.168.1.100',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
    }
  ];
};

const logAuditEvent = (userId: string, action: string, entityType: string, entityId: string, changes: Record<string, any>) => {
  // In a real application, this would send the audit log to the backend
  console.log('Audit Log:', { userId, action, entityType, entityId, changes, timestamp: new Date().toISOString() });
};
